import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../components/seo/SeoHead";

const faqItems = [
  {
    q: "Do you support any size portfolio?",
    a: "Yes. Bellhopt supports any size portfolio, including 1,000+ homes.",
  },
  {
    q: "Can we start with a pilot?",
    a: "Yes. Most enterprise rollouts begin with a pilot before scaling.",
  },
  {
    q: "Do you require integration?",
    a: "No. Integration helps streamline onboarding, but manual onboarding is available.",
  },
  {
    q: "What lead time do you require?",
    a: "24 hours.",
  },
];

const GLASS_CARD =
  "rounded-3xl border border-[#E5E7EB] bg-white/95 backdrop-blur-2xl shadow-[0_24px_80px_rgba(15,23,42,0.12)]";
const GLASS_CARD_SOFT =
  "rounded-3xl border border-[#E5E7EB] bg-white/92 backdrop-blur-2xl shadow-[0_18px_60px_rgba(15,23,42,0.10)]";
const PRIMARY_CTA =
  "rounded-full px-7 py-3 text-white text-[12px] font-semibold tracking-[0.12em] uppercase bg-gradient-to-r from-[#FF4D4D] via-[#FF6B6B] to-[#FF4D4D] shadow-[0_14px_40px_rgba(248,113,113,0.45)] hover:shadow-[0_18px_54px_rgba(248,113,113,0.55)] transition-all";
const SECONDARY_CTA =
  "rounded-full px-7 py-3 text-[#111827] text-[12px] font-semibold tracking-[0.08em] uppercase border border-[#E5E7EB] bg-white hover:bg-[#F9FAFB] transition-all";

function Section({ id, title, children }) {
  return (
    <section id={id} className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
      <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">{title}</h2>
      <div className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">{children}</div>
    </section>
  );
}

function ForEnterprisePropertyManagersPage() {
  const title = "Enterprise Property Managers | Concierge Layer For 1,000+ Homes | Bellhopt";
  const description =
    "Bellhopt supports any size portfolio, including 1,000+ homes. Deliver an arrival-ready standard with Bellhopt Concierge across markets with clear policies and repeatable execution.";
  const canonical = "https://bellhopt.com/for/enterprise-property-managers/";

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Enterprise Guest Experience, Without Enterprise Headcount",
      url: canonical,
      description,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://bellhopt.com/" },
        { "@type": "ListItem", position: 2, name: "For Property Managers", item: "https://bellhopt.com/for/property-managers/" },
        { "@type": "ListItem", position: 3, name: "Enterprise", item: canonical },
      ],
    },
  ];

  return (
    <div className="w-full min-h-screen bg-[#F9FAFB] pt-[72px] font-segoe">
      <SeoHead
        title={title}
        description={description}
        canonical="/for/enterprise-property-managers/"
        robots="index,follow"
        ogTitle="Enterprise Portfolios, Arrival-Ready"
        ogDescription="A concierge execution layer designed for enterprise portfolios that need consistency across markets."
        structuredData={structuredData}
      />

      <main className="w-[90%] max-w-[1100px] m-auto py-10 mobile:w-[95%]">
        <section className={`${GLASS_CARD} p-8 mobile:p-5`}>
          <h1 className="font-segoe font-semibold text-[42px] mobile:text-[30px] text-[#111827] leading-tight">
            Enterprise Guest Experience, Without Enterprise Headcount
          </h1>
          <p className="mt-4 text-[20px] mobile:text-[17px] text-[#6B7280] leading-8">
            Bellhopt supports any size portfolio, including 1,000+ homes. Standardize arrival readiness across markets with a concierge layer that delivers, stocks, and organizes consistently.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>Get Started With Bellhopt</a>
            <Link to="/for/property-managers" className={SECONDARY_CTA}>For Property Managers</Link>
          </div>
          <p className="mt-4 text-[13px] text-[#6B7280]">
            24-hour lead time required. Built for multi-market execution.
          </p>
        </section>

        <div className="mt-6 space-y-6">
          <Section id="enterprise-reality" title="Enterprise Reality">
            <p>At enterprise scale, the challenge is not demand. The challenge is consistency.</p>
            <p className="mt-4">Distributed portfolios create friction:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>access variability</li>
              <li>multi-market vendor reliability</li>
              <li>exception handling</li>
              <li>support ticket volume</li>
            </ul>
            <p className="mt-4">Bellhopt is built to reduce this operational drag.</p>
          </Section>

          <Section id="what-enterprise-gets" title="What Enterprise Teams Get">
            <ul className="list-disc pl-6">
              <li>Portfolio deployment with integration or manual onboarding</li>
              <li>Repeatable execution by Bellhopt Concierge across markets</li>
              <li>Policies that protect reliability, including 24-hour lead time</li>
              <li>Standardization without turning your team into a concierge desk</li>
            </ul>
          </Section>

          <Section id="deployment" title="Deployment Approach">
            <ol className="list-decimal pl-6 space-y-4">
              <li>
                <strong>Phase 1: Pilot</strong>
                <p>Start with a subset of homes and standardize the arrival-ready playbook.</p>
              </li>
              <li>
                <strong>Phase 2: Scale</strong>
                <p>Expand across markets with clear rules and a consistent guest experience.</p>
              </li>
              <li>
                <strong>Phase 3: Optimize</strong>
                <p>Improve conversion, reduce tickets, and standardize messaging.</p>
              </li>
            </ol>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              Deploy Across Your Portfolio
            </h2>
            <p className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              Standardize arrival readiness across 1,000+ homes with repeatable execution.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>Get Started</a>
              <a href="mailto:sales@bellhopt.com" className={SECONDARY_CTA}>Talk To Sales</a>
            </div>
          </section>

          <Section id="why-concierge-wins" title="Why The Concierge Layer Wins">
            <p>Hotels centralize staff and inventory. STR portfolios are distributed.</p>
            <p className="mt-4">A concierge layer bridges the gap:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>it creates consistent execution without needing on-site staff</li>
              <li>it turns guest convenience into a repeatable system</li>
            </ul>
          </Section>

          <Section id="faq" title="FAQ">
            <div className="space-y-3">
              {faqItems.map((item) => (
                <details key={item.q} className="rounded-2xl border border-[#E5E7EB] p-4 bg-white/90 open:bg-[#F9FAFB]">
                  <summary className="cursor-pointer font-segoe font-semibold text-[18px] text-[#111827]">{item.q}</summary>
                  <p className="mt-3 text-[16px] text-[#6B7280] leading-7">{item.a}</p>
                </details>
              ))}
            </div>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">Related Pages</h2>
            <ul className="mt-4 list-disc pl-6 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              <li><Link className="text-primary" to="/for/property-managers">For property managers</Link></li>
              <li><Link className="text-primary" to="/integrations">Integrations</Link></li>
              <li><Link className="text-primary" to="/how-it-works">How it works</Link></li>
              <li><Link className="text-primary" to="/pricing">Pricing</Link></li>
            </ul>
          </section>
        </div>
      </main>
    </div>
  );
}

export default ForEnterprisePropertyManagersPage;
